<script setup>
/**
 * RED-356: what the live Kiirhindamine serves for the explored valuation, and why.
 *
 * Reads the model explorer's `result.served` block: the valuation's owner and their county
 * access, the route that decides it, the comparables model live runs (and the one the admin
 * ran instead), the owner's feature flags next to the global switch and which of them changed
 * the request, the Gate v0 verdict, the gates v1 block (RED-262: status, grade, ranges, σ,
 * inputs), and whether the subject's features came from the admin's
 * 10-minute cache. Admin-facing, Estonian, read-only apart from the rebuild action.
 */
import { computed } from 'vue'

const props = defineProps({
  served: { type: Object, required: true },
  // A test case has no owner by design; an evaluation without one is an orphan.
  isTestCase: { type: Boolean, default: false },
  // The "Ehita tunnused uuesti" run is in flight.
  rebuilding: { type: Boolean, default: false },
  // Plain label per request field (the control card's own), for the "Muudetud" list.
  fieldLabels: { type: Object, default: () => ({}) },
})

const emit = defineEmits(['rebuild-features'])

const FLAG_LABELS = {
  'qv-gate-v0': 'Värav v0',
  'qv-fallback-median': 'Varumediaan',
  'qv-low-price-caution': 'Madala hinna hoiatus',
  'qv-sealed-claims': 'Suletud väited',
  'qv-direct-prediction': 'Otsemudel',
  'segment-configs': 'Segmendi seaded',
  'gates-v1': 'Väravad v1',
  'qv-honest-range': 'Aus ± vahemik (B2)',
}

const MODEL_REASONS = {
  route: 'Üksuse marsruut',
  county: 'Maakonna mudel',
  default: 'Vaikimisi mudel',
  pin_not_served: 'Maakonna mudelit ei serveerita — vaikimisi',
  gates: 'Väravad v1 — maakonna lukus skaala mudel',
}

const ROUTE_STATUSES = {
  promoted: 'edutatud',
  legacy_unverified: 'kontrollimata',
}

const GATE_PATHS = {
  serve_comps: 'võrdlustehingud',
  backup_direct: 'otsemudel (varuvariant)',
}

// RED-262 gates v1: what the block's status and grade mean for the live page.
const GATES_STATUSES = {
  ok: 'hinnatud',
  out_of_scope: 'väljaspool ala — hindaja',
  unavailable: 'puudub — live näitab tänast tulemust',
}

const formatPrice = value =>
  value == null
    ? '–'
    : new Intl.NumberFormat('et-EE', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value)

const ownerLabel = computed(() => {
  const owner = props.served.owner
  if (!owner) {
    return props.isTestCase
      ? 'Testjuhtum — omanikku pole, vaikimisi mudel'
      : 'Omanikku pole — vaikimisi mudel'
  }
  return owner.company ? `${owner.name} · ${owner.company}` : owner.name
})

const accessNotice = computed(
  () =>
    ({
      no_access: 'Omanikul pole selle maakonna ligipääsu — live keelduks; näidatud vaikimisi mudel',
      no_county: 'Hindamise maakonda ei tuvastatud — marsruuti pole; näidatud vaikimisi mudel',
    })[props.served.access] ?? null,
)

const routeLabel = computed(() => {
  const route = props.served.route
  if (!route) {
    return '–'
  }
  if (route.route_id == null) {
    return 'maakonna seadistus'
  }
  return [route.unit, `marsruut #${route.route_id}`, ROUTE_STATUSES[route.status] ?? route.status]
    .filter(Boolean)
    .join(' · ')
})

const routeTitle = computed(() => {
  const route = props.served.route
  if (!route) {
    return undefined
  }
  return [
    `Tee: ${route.path === 'direct' ? 'otsemudel' : 'võrdlustehingud'}`,
    `Mudel marsruudil: ${route.model ?? 'vaikimisi'}`,
    `Hinnaalus: ${route.price_basis}${route.blend_weight != null ? ` (${route.blend_weight})` : ''}`,
    route.tier != null ? `Aste: ${route.tier}` : null,
    route.promotion_id != null ? `Edutus #${route.promotion_id}` : null,
    route.gate_cell ? `Väravarakk: ${route.gate_cell}` : null,
  ]
    .filter(Boolean)
    .join('\n')
})

const model = computed(() => props.served.model || {})
const ranOtherModel = computed(() => model.value.ran && model.value.ran !== model.value.served)

// Which flags actually changed the request live sends, read from what the request carried.
const flagInRequest = key => {
  const request = props.served.request || {}
  return (
    {
      'qv-gate-v0': props.served.gate != null,
      'qv-fallback-median': Boolean(request.fallback_median),
      'qv-low-price-caution': Boolean(request.low_price_caution),
      'qv-sealed-claims': Boolean(request.include_sealed),
      'qv-direct-prediction': props.served.path === 'direct',
      'segment-configs': request.segment_leaf_id != null,
      // RED-262: both ask for `gates`; the honest ± range on the route's own model.
      'gates-v1': Boolean(request.gates) && !request.honest_range,
      'qv-honest-range': Boolean(request.honest_range),
    }[key] ?? false
  )
}

const flags = computed(() =>
  (props.served.flags || []).map(flag => ({
    ...flag,
    label: FLAG_LABELS[flag.key] ?? flag.key,
    differs: Boolean(flag.owner) !== Boolean(flag.global),
    inRequest: flagInRequest(flag.key),
  })),
)

const gate = computed(() => props.served.gate || null)
const gateVerdict = computed(() => gate.value?.verdict || null)
const gateReasons = computed(() => {
  const verdict = gateVerdict.value
  if (!verdict) {
    return ''
  }
  const hard = verdict.hard_human?.length ? verdict.hard_human : verdict.hard || []
  return hard.join(', ')
})

const direct = computed(() => props.served.direct || null)

// RED-262 gates v1: the block live persists (`prediction.gates`), null when not asked.
const gates = computed(() => props.served.gates || null)
const gatesStatus = computed(() => {
  const g = gates.value
  if (!g) {
    return ''
  }
  if (g.status === 'ok') {
    return g.grade === 'valuer' ? 'hindaja — automaatset hinda pole' : `hinne ${g.grade}`
  }
  return GATES_STATUSES[g.status] ?? g.status
})
const gatesStatusClass = computed(() => {
  const g = gates.value
  if (g?.auto_value) {
    return g.grade === 'D' ? 'text-orange-600' : 'text-green-700'
  }
  return g?.status === 'unavailable' ? 'text-gray-500' : 'text-rose-600'
})
const formatPct = value =>
  value == null ? '' : ` (±${new Intl.NumberFormat('et-EE', { maximumFractionDigits: 1 }).format(value)} %)`
const gatesBands = computed(() =>
  ['68', '90']
    .map(level => ({ level, ...(gates.value?.range?.[level] || {}) }))
    .filter(band => band.lo != null && band.hi != null),
)
const decimal = new Intl.NumberFormat('et-EE', { maximumFractionDigits: 3 })
// The spec §1 inputs on one line, as the model saw them.
const gatesInputs = computed(() => {
  const inputs = gates.value?.inputs
  if (!inputs) {
    return ''
  }
  return Object.entries(inputs)
    .map(([key, value]) => `${key} ${value == null ? '–' : decimal.format(value)}`)
    .join(' · ')
})

const overridden = computed(() => props.served.overridden || [])
const overriddenLabel = computed(() => overridden.value.map(key => props.fieldLabels[key] ?? key).join(', '))
</script>

<template>
  <div class="text-[11px] space-y-1.5">
    <div class="text-gray-400">Live kontekst</div>

    <Message v-if="accessNotice" severity="warn" size="small" icon="pi pi-exclamation-triangle">
      {{ accessNotice }}
    </Message>

    <dl class="grid grid-cols-[4.5rem_1fr] gap-x-2 gap-y-0.5">
      <dt class="text-gray-400">Omanik</dt>
      <dd class="text-gray-700 min-w-0 truncate" :title="ownerLabel">{{ ownerLabel }}</dd>

      <dt class="text-gray-400">Maakond</dt>
      <dd class="text-gray-700">{{ served.county || '–' }}</dd>

      <dt class="text-gray-400">Marsruut</dt>
      <dd class="text-gray-700 min-w-0" :class="{ 'cursor-help': routeTitle }" :title="routeTitle">
        {{ routeLabel }}
        <span class="text-gray-400">· {{ served.path === 'direct' ? 'otsemudel' : 'võrdlustehingud' }}</span>
      </dd>

      <dt class="text-gray-400">Mudel</dt>
      <dd class="min-w-0">
        <span class="font-mono text-gray-700 break-all">{{ model.served || '–' }}</span>
        <span
          class="text-gray-500"
          :class="{ 'cursor-help': model.pinned && model.pinned !== model.served }"
          :title="model.pinned && model.pinned !== model.served ? `Maakonna mudel: ${model.pinned}` : undefined"
        >
          · {{ MODEL_REASONS[model.reason] ?? model.reason }}
        </span>
        <div v-if="ranOtherModel" class="text-orange-600">
          Käivitatud: <span class="font-mono break-all">{{ model.ran }}</span> (muudetud)
        </div>
      </dd>

      <template v-if="direct">
        <dt class="text-gray-400">Otsehind</dt>
        <dd class="text-gray-700" :title="direct.model_id ? `Otsemudel: ${direct.model_id}` : undefined">
          <template v-if="direct.available">{{ formatPrice(direct.predicted_value) }}</template>
          <span v-else class="text-rose-600">otsemudel ei vastanud</span>
        </dd>
      </template>

      <template v-if="gate">
        <dt class="text-gray-400">Värav</dt>
        <dd class="text-gray-700 min-w-0">
          <span class="font-mono">{{ gate.cell }}</span>
          · otsehind {{ formatPrice(gate.direct_price) }} ·
          <span
            v-if="gateVerdict"
            class="font-medium"
            :class="gateVerdict.path === 'backup_direct' ? 'text-orange-600' : 'text-green-700'"
          >
            {{ GATE_PATHS[gateVerdict.path] ?? gateVerdict.path }}
          </span>
          <span v-else class="text-gray-400">otsus puudub</span>
          <div v-if="gateReasons" class="text-rose-600">{{ gateReasons }}</div>
        </dd>
      </template>

      <template v-if="gates">
        <dt class="text-gray-400">Väravad</dt>
        <dd class="text-gray-700 min-w-0" data-testid="explorer-gates" :title="gates.version ?? undefined">
          <span class="font-medium" :class="gatesStatusClass">{{ gatesStatus }}</span>
          <template v-if="gates.headline_price != null"> · otsehind {{ formatPrice(gates.headline_price) }}</template>
          <template v-if="gates.sigma != null"> · σ {{ decimal.format(gates.sigma) }}</template>
          <div v-for="band in gatesBands" :key="band.level" class="tabular-nums">
            {{ band.level }} %: {{ formatPrice(band.lo) }} – {{ formatPrice(band.hi) }}{{ formatPct(band.pct) }}
          </div>
          <div v-if="gates.comps_value != null">
            võrdlushind {{ formatPrice(gates.comps_value) }}
            <span class="text-gray-400">· {{ gates.comps_grade_a ? 'võrdlus A' : 'võrdlus pole A' }}</span>
            <span v-if="gates.second_value != null" class="text-green-700"> · teine väärtus</span>
          </div>
          <div v-if="gates.warning === 'wide_range'" class="text-orange-600">lai vahemik</div>
          <div v-if="gates.reason" class="text-rose-600">{{ gates.reason }}</div>
          <div v-if="gatesInputs" class="font-mono text-gray-500 break-all">{{ gatesInputs }}</div>
        </dd>
      </template>

      <template v-if="overridden.length">
        <dt class="text-gray-400">Muudetud</dt>
        <dd class="text-orange-600 min-w-0" :title="overridden.join(', ')">{{ overriddenLabel }}</dd>
      </template>
    </dl>

    <table v-if="flags.length" class="w-full">
      <thead>
        <tr class="text-gray-400 text-left">
          <th class="font-normal">Lipp</th>
          <th class="font-normal text-center w-14">Omanik</th>
          <th class="font-normal text-center w-14">Üldine</th>
          <th
            class="font-normal text-center w-14 cursor-help"
            title="Lipp muutis päringut, mille live saadab (või valis tee / värava)"
          >
            Päringus
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="flag in flags"
          :key="flag.key"
          :class="{ 'bg-amber-50': flag.differs }"
          :title="flag.differs ? `${flag.key}: omaniku väärtus erineb üldisest` : flag.key"
        >
          <td class="text-gray-700">{{ flag.label }}</td>
          <td class="text-center">
            <i class="pi text-[10px]" :class="flag.owner ? 'pi-check text-green-600' : 'pi-minus text-gray-300'" />
          </td>
          <td class="text-center">
            <i class="pi text-[10px]" :class="flag.global ? 'pi-check text-green-600' : 'pi-minus text-gray-300'" />
          </td>
          <td class="text-center">
            <i v-if="flag.inRequest" class="pi pi-arrow-right text-[10px] text-blue-600" />
          </td>
        </tr>
      </tbody>
    </table>

    <div class="flex items-center gap-2 text-gray-500">
      <span>{{ served.features_cached ? 'Tunnused vahemälust' : 'Tunnused ehitatud värskelt' }}</span>
      <Button
        v-if="served.features_cached"
        label="Ehita tunnused uuesti"
        text
        size="small"
        :loading="rebuilding"
        class="w-auto! p-0! text-[11px]!"
        @click="emit('rebuild-features')"
      />
    </div>
  </div>
</template>
